import { useCallback, useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Package, ClipboardList, LogOut, Plus, Trash2, X, Upload, Pencil, BarChart3, Download } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { imgSrc } from "@/components/Catalog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const emptyProduct = { name: "", description: "", price: "", image: "", category: "unisex" };

const STATUS_STYLES = {
  pendiente: "text-gold border-gold/40 bg-gold/10",
  entregado: "text-emerald-400 border-emerald-400/40 bg-emerald-400/10",
  cancelado: "text-red-400 border-red-400/40 bg-red-400/10",
};

const ProductForm = ({ initial, onSave, onCancel, saving }) => {
  const [form, setForm] = useState(initial);
  const [uploading, setUploading] = useState(false);

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/admin/upload`, fd, { withCredentials: true });
      setForm((f) => ({ ...f, image: data.path }));
      toast.success("Imagen subida");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  return (
    <form
      data-testid="product-form"
      onSubmit={(e) => {
        e.preventDefault();
        onSave({ ...form, price: parseFloat(form.price) });
      }}
      className="space-y-5"
    >
      <div>
        <label className="lux-label">Nombre del perfume</label>
        <input data-testid="product-name-input" required className="lux-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ambre Nuit" />
      </div>
      <div>
        <label className="lux-label">Descripción</label>
        <textarea data-testid="product-description-input" rows={2} className="lux-input resize-none" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Notas de ámbar, vainilla..." />
      </div>
      <div>
        <label className="lux-label">Precio (€)</label>
        <input data-testid="product-price-input" required type="number" step="0.01" min="0" className="lux-input" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} placeholder="89.00" />
      </div>
      <div>
        <label className="lux-label mb-2 block">Categoría (Género)</label>
        <div className="flex gap-6 mt-1">
          {["hombre", "mujer", "unisex"].map((cat) => (
            <label key={cat} className="flex items-center gap-2 cursor-pointer text-white text-sm capitalize">
              <input
                type="radio"
                name="category"
                value={cat}
                checked={(form.category || "unisex") === cat}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="accent-gold h-4 w-4 bg-transparent border-white/20"
              />
              {cat}
            </label>
          ))}
        </div>
      </div>
      <div>
        <label className="lux-label">Foto del perfume</label>
        <label
          data-testid="product-image-upload"
          className="flex items-center justify-center gap-3 border border-dashed border-white/20 hover:border-gold px-4 py-8 cursor-pointer transition-colors text-neutral-400 hover:text-gold text-sm"
        >
          <Upload size={16} />
          {uploading ? "Subiendo..." : form.image ? "Imagen lista — cambiar" : "Subir imagen"}
          <input type="file" accept="image/*" className="hidden" onChange={uploadImage} />
        </label>
        {form.image && (
          <div className="mt-4 w-24 aspect-[3/4] overflow-hidden border border-white/10">
            <img src={imgSrc(form.image)} alt="Vista previa" className="w-full h-full object-cover" />
          </div>
        )}
      </div>
      <div className="flex gap-4 pt-2">
        <button data-testid="product-save-button" type="submit" disabled={saving || uploading || !form.image} className="flex-1 bg-gold text-black text-xs uppercase tracking-[0.25em] py-3.5 hover:bg-gold-light transition-colors disabled:opacity-40">
          {saving ? "Guardando..." : "Guardar perfume"}
        </button>
        <button data-testid="product-cancel-button" type="button" onClick={onCancel} className="px-6 border border-white/20 text-neutral-400 text-xs uppercase tracking-[0.25em] hover:border-gold hover:text-gold transition-colors">
          Cancelar
        </button>
      </div>
    </form>
  );
};

const ProductsTab = () => {
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null); // null | "new" | product
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/products`).then((r) => setProducts(r.data)).catch(() => {});
  }, []);
  useEffect(load, [load]);

  const save = async (form) => {
    setSaving(true);
    try {
      if (editing === "new") {
        await axios.post(`${API}/admin/products`, form, { withCredentials: true });
        toast.success("Perfume publicado");
      } else {
        await axios.put(`${API}/admin/products/${editing.id}`, form, { withCredentials: true });
        toast.success("Perfume actualizado");
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/admin/products/${id}`, { withCredentials: true });
      toast.success("Perfume eliminado");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-products-tab">
      <div className="flex items-center justify-between mb-8">
        <h2 className="font-serif-lux text-2xl tracking-[0.15em] uppercase text-white">Perfumes ({products.length})</h2>
        <button
          data-testid="new-product-button"
          onClick={() => setEditing("new")}
          className="flex items-center gap-2 bg-gold text-black text-[11px] uppercase tracking-[0.2em] px-5 py-3 hover:bg-gold-light transition-colors"
        >
          <Plus size={14} /> Nuevo perfume
        </button>
      </div>

      <AnimatePresence>
        {editing && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.4 }}
            className="overflow-hidden mb-10"
          >
            <div className="border border-gold/30 bg-noir-soft p-8 max-w-xl">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-serif-lux text-xl tracking-[0.15em] uppercase text-gold">{editing === "new" ? "Nuevo perfume" : "Editar perfume"}</h3>
                <button onClick={() => setEditing(null)} className="text-neutral-500 hover:text-gold transition-colors" aria-label="Cerrar formulario"><X size={18} /></button>
              </div>
              <ProductForm
                initial={editing === "new" ? emptyProduct : { name: editing.name, description: editing.description, price: String(editing.price), image: editing.image, category: editing.category || "unisex" }}
                onSave={save}
                onCancel={() => setEditing(null)}
                saving={saving}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="grid gap-4">
        {products.map((p) => (
          <div key={p.id} data-testid={`admin-product-row-${p.id}`} className="flex items-center gap-5 border border-white/10 bg-noir-soft px-5 py-4 hover:border-gold/30 transition-colors">
            <div className="w-14 aspect-[3/4] overflow-hidden border border-white/10 shrink-0">
              <img src={imgSrc(p.image)} alt={p.name} className="w-full h-full object-cover" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-serif-lux text-lg tracking-[0.1em] uppercase text-white truncate">{p.name}</h3>
              <p className="text-neutral-500 text-xs font-light truncate">{p.description}</p>
            </div>
            <span className="text-gold font-serif-lux text-lg shrink-0">{p.price.toFixed(2)} €</span>
            <button data-testid={`edit-product-${p.id}`} onClick={() => setEditing(p)} className="text-neutral-500 hover:text-gold transition-colors shrink-0" aria-label="Editar">
              <Pencil size={16} />
            </button>
            <button data-testid={`delete-product-${p.id}`} onClick={() => remove(p.id)} className="text-neutral-600 hover:text-red-400 transition-colors shrink-0" aria-label="Eliminar">
              <Trash2 size={16} />
            </button>
          </div>
        ))}
        {products.length === 0 && <p className="text-neutral-500 font-light text-center py-12">Aún no hay perfumes. Publica el primero.</p>}
      </div>
    </div>
  );
};

const OrdersTab = () => {
  const [orders, setOrders] = useState([]);

  const load = useCallback(() => {
    axios.get(`${API}/admin/orders`, { withCredentials: true }).then((r) => setOrders(r.data)).catch(() => {});
  }, []);
  useEffect(load, [load]);

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/orders/${id}`, { status }, { withCredentials: true });
      toast.success("Estado actualizado");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const exportToCSV = () => {
    if (orders.length === 0) {
      toast.error("No hay pedidos para exportar");
      return;
    }
    const headers = ["ID Pedido", "Fecha", "Cliente", "Email", "Telefono", "Direccion", "Ciudad", "CP", "Productos", "Total", "Estado"];
    const rows = orders.map(o => [
      o.id.toUpperCase(),
      new Date(o.created_at).toLocaleString("es-ES"),
      `${o.customer.name} ${o.customer.surname}`,
      o.customer.email,
      o.customer.phone,
      o.customer.address,
      o.customer.city,
      o.customer.postal_code,
      o.items.map(i => `${i.name} (x${i.quantity})`).join("; "),
      o.total,
      o.status
    ]);
    
    let csvContent = "\uFEFF"; // UTF-8 BOM
    csvContent += headers.join(",") + "\n";
    rows.forEach(r => {
      const escapedRow = r.map(val => `"${String(val).replace(/"/g, '""')}"`);
      csvContent += escapedRow.join(",") + "\n";
    });
    
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `maison_elixir_pedidos_${new Date().toISOString().slice(0,10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("Pedidos exportados");
  };

  return (
    <div data-testid="admin-orders-tab">
      <div className="flex items-center justify-between mb-8">
        <h2 className="font-serif-lux text-2xl tracking-[0.15em] uppercase text-white">Pedidos ({orders.length})</h2>
        <button
          onClick={exportToCSV}
          className="flex items-center gap-2 border border-gold/40 text-gold text-[11px] uppercase tracking-[0.2em] px-5 py-3 hover:bg-gold hover:text-black transition-all"
        >
          <Download size={14} /> Exportar a Excel
        </button>
      </div>
      <div className="space-y-5">
        {orders.map((o) => (
          <div key={o.id} data-testid={`admin-order-${o.id}`} className="border border-white/10 bg-noir-soft p-6">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <p className="text-gold font-serif-lux text-lg">#{o.id.slice(0, 8).toUpperCase()}</p>
                <p className="text-neutral-500 text-xs mt-1">{new Date(o.created_at).toLocaleString("es-ES")}</p>
              </div>
              <select
                data-testid={`order-status-${o.id}`}
                value={o.status}
                onChange={(e) => setStatus(o.id, e.target.value)}
                className={`text-[11px] uppercase tracking-[0.2em] border px-4 py-2 bg-transparent cursor-pointer focus:outline-none ${STATUS_STYLES[o.status]}`}
              >
                <option value="pendiente" className="bg-noir-soft">Pendiente</option>
                <option value="entregado" className="bg-noir-soft">Entregado</option>
                <option value="cancelado" className="bg-noir-soft">Cancelado</option>
              </select>
            </div>
            <div className="grid sm:grid-cols-2 gap-6 mt-5 text-sm">
              <div>
                <p className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 mb-2">Cliente</p>
                <p className="text-white">{o.customer.name} {o.customer.surname}</p>
                <p className="text-neutral-400 font-light">{o.customer.email}</p>
                <p className="text-neutral-400 font-light">{o.customer.phone}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 mb-2">Entrega</p>
                <p className="text-neutral-300 font-light">{o.customer.address}</p>
                <p className="text-neutral-400 font-light">{o.customer.city} — {o.customer.postal_code}</p>
                {o.customer.notes && <p className="text-neutral-500 font-light mt-1 text-xs">Notas: {o.customer.notes}</p>}
              </div>
            </div>
            <div className="border-t border-white/10 mt-5 pt-4 space-y-1">
              {o.items.map((i, idx) => (
                <div key={idx} className="flex justify-between text-sm font-light">
                  <span className="text-neutral-300">{i.name} × {i.quantity}</span>
                  <span className="text-neutral-400">{(i.price * i.quantity).toFixed(2)} €</span>
                </div>
              ))}
              <div className="flex justify-between pt-3 mt-2 border-t border-white/10">
                <span className="text-[10px] uppercase tracking-[0.25em] text-neutral-500">Cobrar contra reembolso</span>
                <span className="font-serif-lux text-xl text-gold">{o.total.toFixed(2)} €</span>
              </div>
            </div>
          </div>
        ))}
        {orders.length === 0 && <p className="text-neutral-500 font-light text-center py-12" data-testid="orders-empty">Todavía no hay pedidos.</p>}
      </div>
    </div>
  );
};

const StatsTab = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/admin/orders`, { withCredentials: true })
      .then((r) => {
        setOrders(r.data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="text-center py-12">
        <span className="text-gold font-serif-lux tracking-[0.2em] uppercase animate-pulse">Cargando estadísticas...</span>
      </div>
    );
  }

  // Métricas
  const completedOrders = orders.filter(o => o.status === "entregado");
  const pendingOrders = orders.filter(o => o.status === "pendiente");
  const cancelledOrders = orders.filter(o => o.status === "cancelado");

  const totalBilling = completedOrders.reduce((sum, o) => sum + o.total, 0);

  // Agrupado por mes
  const salesByMonth = {};
  completedOrders.forEach(o => {
    const date = new Date(o.created_at);
    const month = date.toLocaleString("es-ES", { month: "long", year: "numeric" });
    salesByMonth[month] = (salesByMonth[month] || 0) + o.total;
  });

  return (
    <div data-testid="admin-stats-tab" className="space-y-10">
      <h2 className="font-serif-lux text-2xl tracking-[0.15em] uppercase text-white mb-8">Estadísticas de Venta</h2>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="border border-gold/30 bg-noir-soft p-6">
          <p className="text-[10px] uppercase tracking-[0.25em] text-neutral-400 mb-2">Facturación Total</p>
          <p className="font-serif-lux text-3xl text-gold">{totalBilling.toFixed(2)} €</p>
        </div>
        <div className="border border-white/10 bg-noir-soft p-6">
          <p className="text-[10px] uppercase tracking-[0.25em] text-neutral-400 mb-2">Entregados</p>
          <p className="font-serif-lux text-3xl text-white">{completedOrders.length}</p>
        </div>
        <div className="border border-white/10 bg-noir-soft p-6">
          <p className="text-[10px] uppercase tracking-[0.25em] text-neutral-400 mb-2">Pendientes</p>
          <p className="font-serif-lux text-3xl text-white">{pendingOrders.length}</p>
        </div>
        <div className="border border-white/10 bg-noir-soft p-6">
          <p className="text-[10px] uppercase tracking-[0.25em] text-neutral-400 mb-2">Cancelados</p>
          <p className="font-serif-lux text-3xl text-white">{cancelledOrders.length}</p>
        </div>
      </div>

      <div className="border border-white/10 bg-noir-soft p-8">
        <h3 className="font-serif-lux text-lg tracking-[0.15em] uppercase text-white mb-6">Facturación por Mes</h3>
        <div className="space-y-4">
          {Object.entries(salesByMonth).map(([month, amount]) => (
            <div key={month} className="flex items-center justify-between border-b border-white/5 pb-2">
              <span className="text-neutral-300 font-light capitalize">{month}</span>
              <span className="font-serif-lux text-lg text-gold">{amount.toFixed(2)} €</span>
            </div>
          ))}
          {Object.keys(salesByMonth).length === 0 && (
            <p className="text-neutral-500 font-light text-center py-6">No hay datos de ventas completadas todavía.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default function Admin() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("products");

  useEffect(() => {
    if (user === false) navigate("/login");
  }, [user, navigate]);

  if (!user) {
    return (
      <div className="min-h-screen bg-noir flex items-center justify-center">
        <span className="text-gold font-serif-lux tracking-[0.3em] uppercase animate-pulse">Cargando...</span>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-noir" data-testid="admin-dashboard">
      <header className="border-b border-white/10 backdrop-blur-xl bg-black/60 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-6 h-20 flex items-center justify-between">
          <Link to="/" data-testid="admin-logo-link" className="font-serif-lux text-xl tracking-[0.35em] text-white hover:text-gold transition-colors">
            MÉ <span className="text-[9px] uppercase tracking-[0.4em] text-neutral-500 ml-2">Panel Admin</span>
          </Link>
          <div className="flex items-center gap-6">
            <Link to="/" data-testid="admin-view-store-link" className="text-[11px] uppercase tracking-[0.2em] text-neutral-400 hover:text-gold transition-colors">Ver tienda</Link>
            <button
              data-testid="admin-logout-button"
              onClick={async () => { await logout(); navigate("/"); }}
              className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-neutral-400 hover:text-red-400 transition-colors"
            >
              <LogOut size={14} /> Salir
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-12">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <div className="flex gap-4 mb-12">
            <button
              data-testid="tab-products-button"
              onClick={() => setTab("products")}
              className={`flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] px-6 py-3 border transition-colors duration-300 ${tab === "products" ? "border-gold text-gold bg-gold/10" : "border-white/15 text-neutral-400 hover:border-gold/50 hover:text-gold"}`}
            >
              <Package size={14} /> Perfumes
            </button>
            <button
              data-testid="tab-orders-button"
              onClick={() => setTab("orders")}
              className={`flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] px-6 py-3 border transition-colors duration-300 ${tab === "orders" ? "border-gold text-gold bg-gold/10" : "border-white/15 text-neutral-400 hover:border-gold/50 hover:text-gold"}`}
            >
              <ClipboardList size={14} /> Pedidos
            </button>
            <button
              onClick={() => setTab("stats")}
              className={`flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] px-6 py-3 border transition-colors duration-300 ${tab === "stats" ? "border-gold text-gold bg-gold/10" : "border-white/15 text-neutral-400 hover:border-gold/50 hover:text-gold"}`}
            >
              <BarChart3 size={14} /> Estadísticas
            </button>
          </div>
          {tab === "products" ? <ProductsTab /> : tab === "orders" ? <OrdersTab /> : <StatsTab />}
        </motion.div>
      </main>
    </div>
  );
}
